// =============================================================================
// SIREPPAR - Página de Login con Opciones de Inicio
// =============================================================================
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { URL_PLANTILLA_AFILIADOS } from '../../services/api';
import { TEXTO_CIERRE_REEMPADRONAMIENTO } from '../../utils/cierreReempadronamiento';

interface LoginFormData {
  email: string;
  password: string;
  mfaCode?: string;
}

type ViewMode = 'initial' | 'reempadronamiento' | 'login';

// La pantalla inicial muestra 2 tarjetas fijas —reempadronamiento (o el bloque de
// cierre que lo reemplaza) y ACCEDER AL SISTEMA— más las que habiliten los flags.
// La grilla se adapta a esa cantidad: con las clases de 4 columnas fijas, en
// producción (los dos flags en false) quedaban 2 tarjetas ocupando media fila.
const CONSTITUCION_HABILITADA = import.meta.env.VITE_CONSTITUCION_HABILITADA === 'true';
// Reemplaza a la extinta tarjeta "CONSTITUIR NUEVA ALIANZA": venció el plazo para
// constituir alianzas, ahora ese lugar lo ocupa el acceso a Candidaturas.
const CANDIDATURAS_HABILITADA = import.meta.env.VITE_CANDIDATURAS_HABILITADA === 'true';
const TARJETAS_VISIBLES = 2 + (CONSTITUCION_HABILITADA ? 1 : 0) + (CANDIDATURAS_HABILITADA ? 1 : 0);

const GRID_TARJETAS =
  TARJETAS_VISIBLES >= 4
    ? 'grid sm:grid-cols-2 xl:grid-cols-4 gap-6 max-w-6xl mx-auto'
    : TARJETAS_VISIBLES === 3
      ? 'grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto'
      : 'grid md:grid-cols-2 gap-8 max-w-3xl mx-auto';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('initial');
  const [showMFA, setShowMFA] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // true cuando se entró al formulario tocando la tarjeta "Candidaturas": si
  // el que se loguea resulta apoderado, lo mandamos a su panel de
  // candidaturas en vez del Dashboard. Un usuario interno entra igual que
  // siempre (Dashboard), sin importar por qué tarjeta llegó al formulario.
  const [vieneDeCandidaturas, setVieneDeCandidaturas] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await login(data);
      if (result.requiresMFA) {
        setShowMFA(true);
        toast.success('Ingrese el código de verificación');
      } else {
        toast.success('Bienvenido al sistema');
        const esApoderado = result.user?.rol === 'APODERADO_FIRMANTE';
        navigate(vieneDeCandidaturas && esApoderado ? '/apoderado/panel' : '/dashboard');
      }
    } catch (error: any) {
      const message = error.response?.data?.error?.message || 'Error al iniciar sesión';
      toast.error(message);
      if (showMFA) {
        setValue('mfaCode', '');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Vista inicial con 2 botones grandes
  if (viewMode === 'initial') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 flex items-center justify-center p-4">
        <div className="max-w-6xl w-full">
          {/* Logo y título */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-xl mb-6">
              <svg className="w-12 h-12 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">SIREPPAR</h1>
            <p className="text-primary-200 text-lg">Sistema de Registro Permanente de Partidos Políticos</p>
            <p className="text-primary-300 text-sm mt-1">Secretaría Electoral Permanente - Provincia del Chubut</p>
          </div>

          {/* Tarjetas de acceso: cantidad variable según los flags */}
          <div className={GRID_TARJETAS}>
            {/* Botón Reempadronamiento o bloque informativo de cierre */}
            {import.meta.env.VITE_REEMPADRONAMIENTO_PUBLICO === 'true' ? (
              <button
                onClick={() => setViewMode('reempadronamiento')}
                className="bg-white rounded-2xl p-10 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] group"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-green-200 transition">
                    <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">INICIAR REEMPADRONAMIENTO</h2>
                  <p className="text-gray-500 text-sm mb-4">
                    Registre su partido político en el sistema electoral provincial
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-left">
                    <p className="text-amber-800 text-xs">
                      <strong>Importante:</strong> Su usuario y contraseña se crearán durante este proceso.
                    </p>
                  </div>
                  <span className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-xl font-medium group-hover:bg-green-700 transition">
                    Comenzar
                    <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </div>
              </button>
            ) : (
              <div className="bg-white rounded-2xl p-10 shadow-xl">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mb-6">
                    <svg className="w-10 h-10 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">REEMPADRONAMIENTO FINALIZADO</h2>
                  <p className="text-gray-500 text-sm mb-4">
                    {TEXTO_CIERRE_REEMPADRONAMIENTO}
                  </p>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-6">
                    <p className="text-gray-600 text-xs">
                      Si ya completó el reempadronamiento, ingrese al sistema con su usuario.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Botón Ya tengo usuario */}
            <button
              onClick={() => setViewMode('login')}
              className="bg-white rounded-2xl p-10 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] group"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 bg-primary-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary-200 transition">
                  <svg className="w-10 h-10 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-3">ACCEDER AL SISTEMA</h2>
                <p className="text-gray-500 text-sm mb-4">
                  Ingrese con su usuario y contraseña
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                  <p className="text-blue-800 text-xs">
                    Solo si ya completó el reempadronamiento
                  </p>
                </div>
                <span className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-xl font-medium group-hover:bg-primary-700 transition">
                  Ingresar
                  <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </div>
            </button>
          {/* Botón Constitución de Nuevo Partido (flag VITE_CONSTITUCION_HABILITADA) */}
            {CONSTITUCION_HABILITADA && (
            <a
              href="/constitucion"
              className="block bg-white rounded-2xl p-10 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] group"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 bg-amber-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-amber-200 transition">
                  <svg className="w-10 h-10 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-3">CONSTITUIR NUEVO PARTIDO</h2>
                <p className="text-gray-500 text-sm mb-4">
                  Inicie el proceso de reconocimiento de una nueva agrupación política
                </p>
                <span className="inline-flex items-center px-6 py-3 bg-amber-500 text-white rounded-xl font-medium group-hover:bg-amber-600 transition">
                  Comenzar
                  <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </div>
            </a>
            )}

            {/* Botón Candidaturas: mismo login de siempre (cualquier rol
                puede entrar), pero si el que se loguea es apoderado lo manda
                a su panel de candidaturas en vez del Dashboard. Flag
                VITE_CANDIDATURAS_HABILITADA. */}
            {CANDIDATURAS_HABILITADA && (
            <button
              onClick={() => {
                setVieneDeCandidaturas(true);
                setViewMode('login');
              }}
              className="relative block w-full bg-white rounded-2xl p-10 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] group border-2 border-indigo-700"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 bg-indigo-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-indigo-200 transition">
                  <svg className="w-10 h-10 text-indigo-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-3">PRESENTAR CANDIDATURAS</h2>
                <p className="text-gray-500 text-sm mb-4">
                  Cargue y siga el estado de las candidaturas de su partido o alianza
                </p>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-6">
                  <p className="text-indigo-800 text-xs">
                    Requiere iniciar sesión con su usuario
                  </p>
                </div>
                <span className="inline-flex items-center px-6 py-3 bg-indigo-700 text-white rounded-xl font-medium group-hover:bg-indigo-800 transition">
                  Ingresar
                  <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </div>
            </button>
            )}
          </div>
          {/* Footer */}
          <p className="text-center text-primary-300 text-sm mt-8">
            © 2026 Secretaría Electoral Permanente del Chubut - Todos los derechos reservados
          </p>
        </div>
      </div>
    );
  }

  // Vista de preparación para reempadronamiento
  if (viewMode === 'reempadronamiento') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-green-900 flex items-center justify-center p-4">
        <div className="max-w-4xl w-full">
          {/* Botón volver */}
          <button
            onClick={() => setViewMode('initial')}
            className="flex items-center text-white/80 hover:text-white mb-6 transition"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver al inicio
          </button>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-green-600 px-8 py-6">
              <h1 className="text-2xl font-bold text-white">Antes de comenzar el Reempadronamiento</h1>
              <p className="text-green-100 mt-1">Por favor lea atentamente las siguientes instrucciones</p>
            </div>

            <div className="p-8">
              {/* Alerta importante */}
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-8">
                <div className="flex">
                  <svg className="w-6 h-6 text-amber-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <h3 className="font-bold text-amber-800">Importante: Planilla de Afiliados</h3>
                    <p className="text-amber-700 mt-1">
                      Antes de empezar el reempadronamiento, complete el Excel con los datos de los afiliados. Este documento será solicitado en el paso 7 del proceso.
                      <strong> No se aceptarán planillas que no cumplan con el formato establecido.</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Descarga de Excel */}
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Paso 1: Descargue la planilla de afiliados
                </h3>
                <p className="text-gray-600 mb-4">
                  Descargue el modelo de Excel y complete los datos de todos los afiliados de su partido.
                  El archivo debe ser exactamente igual que el modelo, con las mismas columnas definidas.
                </p>
                <a
                  href={URL_PLANTILLA_AFILIADOS}
                  download
                  className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Descargar Planilla de Afiliados (Excel)
                </a>
              </div>

              {/* Video tutorial */}
              <div className="mb-8">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Paso 2: Mire el video tutorial antes de comenzar
                </h3>
                <p className="text-gray-600 mb-4">
                  Este video le guiará paso a paso en el proceso de reempadronamiento.
                </p>
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <video
                    className="absolute top-0 left-0 w-full h-full rounded-xl"
                    controls
                    preload="metadata"
                  >
                    <source src="https://sireppar-assets-public.s3.us-east-1.amazonaws.com/videos/ReempadronamientoHorizontal.mp4" type="video/mp4" />
                    Su navegador no soporta la reproducción de video.
                  </video>
                </div>
              </div>

              {/* Botón para iniciar */}
              <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-red-600 text-xl">⚠️</span>
                  <div>
                    <h3 className="font-bold text-red-800">No actualice ni cierre la página</h3>
                    <p className="text-red-700 text-sm mt-1">
                      Una vez iniciado el reempadronamiento, <strong>no presione F5, no actualice la página ni cierre el navegador</strong>. Si lo hace, perderá todos los datos cargados y deberá comenzar la carga desde cero.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-blue-600 text-xl">📧</span>
                  <div>
                    <h3 className="font-bold text-blue-800">Al finalizar el trámite</h3>
                    <p className="text-blue-700 text-sm mt-1">
                      Al completar el último paso y presionar <strong>"Enviar"</strong>, se descargará automáticamente un PDF con la declaración jurada de la información presentada. Además, recibirá por correo electrónico la confirmación de recepción de la documentación.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6">
                <Link
                  to="/reempadronamiento"
                  className="w-full flex items-center justify-center px-8 py-4 bg-green-600 text-white rounded-xl hover:bg-green-700 transition font-bold text-lg"
                >
                  Ya descargué el Excel y vi el tutorial, comenzar Reempadronamiento
                  <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vista de login
  return (
    <div className="min-h-screen flex">
      {/* Panel izquierdo - Información */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 p-12 flex-col justify-between">
        <div>
          <button
            onClick={() => setViewMode('initial')}
            className="flex items-center text-white/80 hover:text-white mb-8 transition"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver al inicio
          </button>
          
          <div className="flex items-center space-x-4 mb-12">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">SIREPPAR</h1>
              <p className="text-primary-200 text-sm">Sistema de Registro Permanente</p>
            </div>
          </div>

          <h2 className="text-4xl font-bold text-white leading-tight mb-6">
            Gestión Electoral<br />Moderna y Segura
          </h2>
          <p className="text-primary-200 text-lg">
            Plataforma integral para la administración del ciclo
            de vida de los partidos políticos.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-3xl font-bold text-white">100%</p>
            <p className="text-primary-200 text-sm">Digital</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-3xl font-bold text-white">24/7</p>
            <p className="text-primary-200 text-sm">Disponible</p>
          </div>
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-3xl font-bold text-white">TLS</p>
            <p className="text-primary-200 text-sm">Cifrado</p>
          </div>
        </div>
      </div>

      {/* Panel derecho - Formulario */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Botón volver en móvil */}
          <button
            onClick={() => setViewMode('initial')}
            className="lg:hidden flex items-center text-gray-600 hover:text-gray-900 mb-6 transition"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver al inicio
          </button>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Iniciar Sesión</h2>
              <p className="text-gray-500 mt-2">Ingrese sus credenciales para acceder al sistema</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {!showMFA ? (
                <>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Correo electrónico
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      {...register('email', {
                        required: 'El correo es requerido',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Correo electrónico inválido',
                        },
                      })}
                      className={`w-full px-4 py-3 rounded-lg border ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-2 focus:ring-primary-500 focus:border-transparent transition`}
                      placeholder="usuario@ejemplo.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      Contraseña
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        {...register('password', {
                          required: 'La contraseña es requerida',
                          minLength: {
                            value: 8,
                            message: 'La contraseña debe tener al menos 8 caracteres',
                          },
                        })}
                        className={`w-full px-4 py-3 rounded-lg border ${
                          errors.password ? 'border-red-500' : 'border-gray-300'
                        } focus:ring-2 focus:ring-primary-500 focus:border-transparent transition pr-12`}
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>
                    )}
                  </div>
                </>
              ) : (
                <div>
                  <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700 mb-1">
                    Código de verificación
                  </label>
                  <input
                    id="mfaCode"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    {...register('mfaCode', {
                      required: 'El código es requerido',
                      pattern: {
                        value: /^\d{6}$/,
                        message: 'El código debe tener 6 dígitos',
                      },
                    })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition text-center text-2xl tracking-widest"
                    placeholder="000000"
                  />
                  {errors.mfaCode && (
                    <p className="mt-1 text-sm text-red-500">{errors.mfaCode.message}</p>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Verificando...
                  </span>
                ) : showMFA ? (
                  'Verificar código'
                ) : (
                  'Ingresar'
                )}
              </button>

              {!showMFA && (
                <div className="text-center mt-4">
                  <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">
                    ¿Olvidó su contraseña?
                  </Link>
                </div>
              )}

              {showMFA && (
                <button
                  type="button"
                  onClick={() => setShowMFA(false)}
                  className="w-full text-gray-600 py-2 hover:text-gray-800 transition"
                >
                  ← Volver al inicio de sesión
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}